import {
  AlertComponent,
  AttachedDesignUploadNotice,
  BasicInfoComponent,
  FormSection,
  ModalComponent,
  NavBar,
  PdfTallyFile,
  ShowContent,
  Spinner,
  TallyTable,
  UploadFileForm,
  WBDDesign,
} from "../../components";
import {
  useAttachedDesignUpload,
  useFetch,
  usePermissions,
  useUploadedDocumentDownload,
} from "../../hooks";
import {
  ClientProps,
  DocumentProps,
  ProductProps,
} from "../../interfaces/interfaces";
import styles from "../main.module.sass";
import { Box, Container, Fade } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import DataContext from "../../context/DataContext";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { PDFView } from "../../components/pdf";
import SellIcon from "@mui/icons-material/Sell";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import { formatDesignPdfFileName } from "../../utils/fileNames";

interface TechProposalInfoProps {
  clients: ClientProps[];
  products: ProductProps[];
  customTools: ProductProps[];
}

interface FetchResponse {
  success?: boolean;
  data: TechProposalInfoProps;
}

export const TallyDesignForm = () => {
  let [searchParams] = useSearchParams();
  const pdf = searchParams.get("pdf");
  const { data, error, isLoading } = useFetch<FetchResponse>("tech-proposal");
  const [inEdit, setInEdit] = useState(false);
  const [inPdf, setInPdf] = useState(pdf || false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [isUploadSuccess, setIsUploadSuccess] = useState(false);
  const [isUploadError, setIsUploadError] = useState(false);
  const { idTally } = useParams();
  const [products, setProducts] = useState<ProductProps[]>([]);
  const [customTools, setCustomTools] = useState<ProductProps[]>([]);
  const {
    fetchDataClient,
    toSave,
    onSaveTally,
    onEditTally,
    onCancelEdition,
    fetchDataTally,
    fetchDataProducts,
    onResetValues,
    onCreatePdf,
    isSuccess,
    isError,
    data: pdfData,
  } = useContext(DataContext);
  const navigate = useNavigate();
  const { canManageDesigns } = usePermissions();
  const savedTallyId = idTally && idTally !== "0" ? parseInt(idTally) : null;
  const { attachedUpload, refreshAttachedUpload } = useAttachedDesignUpload({
    endpointBase: "tally-list",
    designId: savedTallyId,
    designIdField: "tallyId",
    wellId: pdfData.basicInfo.well?.id,
    enabled: Boolean(savedTallyId),
  });
  const { downloadUploadedDocument, isDownloadingUploadedDocument } =
    useUploadedDocumentDownload("pdf-tally-adm");

  useEffect(() => {
    fetchDataClient();
    if (idTally && idTally !== "0") {
      fetchDataTally(parseInt(idTally));
      setInEdit(true);
    } else {
      onResetValues();
    }
  }, [idTally]);

  useEffect(() => {
    fetchDataProducts();
  }, []);

  useEffect(() => {
    if (data) {
      const products = data ? data.data.products : [];
      const customTools = data ? data.data.customTools : [];
      setProducts(products);
      setCustomTools(customTools);
    }
  }, [data]);

  useEffect(() => {
    setTimeout(() => {
      setIsUploadSuccess(false);
      setIsUploadError(false);
    }, 4000);
  }, [isUploadSuccess, isUploadError]);

  const handleUploadTally = async ({
    client,
    well,
    customName,
    file,
    installationDate,
    tallyId,
  }: DocumentProps) => {
    try {
      setIsUploadingDocument(true);
      setIsUploadModalOpen(false);
      const formData = new FormData();
      formData.append("client", JSON.stringify(client));
      formData.append("well", JSON.stringify(well));
      formData.append("customName", customName || pdfData.basicInfo.customName);
      installationDate &&
        formData.append("installationDate", installationDate.toISOString());
      tallyId && formData.append("tallyId", String(tallyId));
      formData.append("type", "tally");
      formData.append("file", file, file.name);
      const response = await fetch(
        process.env.REACT_APP_DEV_API + "/tally-upload",
        { method: "POST", body: formData },
      );
      const res = await response.json();
      if (res.success) {
        setIsUploadSuccess(true);
        refreshAttachedUpload();
      } else setIsUploadError(true);
    } catch {
      setIsUploadError(true);
    } finally {
      setIsUploadingDocument(false);
    }
  };

  const buttons = [
    {
      title: "Cancel",
      action: onCancelEdition,
      icon: <CancelIcon />,
      disabled: !!inPdf,
    },
    ...(canManageDesigns
      ? [
          {
            title: "Order",
            action: () => navigate(`/sales/tally/${idTally}`),
            icon: <SellIcon />,
            disabled: !toSave,
          },
          {
            title: "Save",
            action: inEdit ? () => idTally && onEditTally(idTally) : onSaveTally,
            icon: <SaveIcon />,
            disabled: !toSave,
          },
          {
            title: "Upload",
            action: () => setIsUploadModalOpen(true),
            icon: <DriveFolderUploadIcon />,
            disabled:
              !pdfData.basicInfo.client?.id || !pdfData.basicInfo.well?.id,
          },
        ]
      : []),
    {
      title: !inPdf ? "To Pdf" : "Go Back",
      action: () => {
        onCreatePdf();
        setInPdf(!inPdf);
      },
      icon: !inPdf ? <PictureAsPdfIcon /> : <ArrowBackIosIcon />,
    },
  ];

  return (
    <>
      <NavBar title="Tally & WBD" buttons={buttons} />

      {inPdf ? (
        <Box sx={{ marginTop: "80px" }}>
          <PDFView
            fileName={formatDesignPdfFileName({
              title: "TALLY DESIGN",
              wellName: pdfData.basicInfo.well.name,
              designName: pdfData.basicInfo.customName,
            })}
            document={(us) => <PdfTallyFile data={pdfData} unitSystem={us} />}
            action={pdf ? onCreatePdf : () => {}}
          />
        </Box>
      ) : (
        <Box
          sx={{
            marginTop: "80px",
            minHeight: "calc(100vh - 80px)",
            background:
              "linear-gradient(135deg, rgba(241, 236, 231, 0.8) 0%, rgba(241, 236, 231, 0.4) 100%)",
            padding: { xs: 2, md: 4 },
            overflowX: "hidden",
          }}
        >
          <Container maxWidth="xl">
            <Fade in={true} timeout={600}>
              <Box>
                <AttachedDesignUploadNotice
                  designLabel="tally design"
                  isDownloading={isDownloadingUploadedDocument}
                  upload={attachedUpload}
                  onDownload={() =>
                    attachedUpload &&
                    downloadUploadedDocument(
                      attachedUpload.path,
                      attachedUpload.customName,
                    )
                  }
                  onReplace={() => setIsUploadModalOpen(true)}
                />

                <FormSection title="Well Information" icon={<InfoOutlinedIcon />}>
                  <BasicInfoComponent />
                </FormSection>

                <FormSection title="Tally Design" icon={<ListAltIcon />}>
                  <TallyTable products={products} customTools={customTools} />
                </FormSection>

                <FormSection title="Wellbore Design" icon={<AccountTreeIcon />}>
                  <WBDDesign />
                </FormSection>
              </Box>
            </Fade>
          </Container>
        </Box>
      )}

      {isSuccess && <AlertComponent type="success" />}
      {isError && <AlertComponent type="error" />}
      {isUploadSuccess && <AlertComponent type="success" />}
      {isUploadError && <AlertComponent type="error" />}
      {isUploadingDocument && <div className={styles.backdrop}></div>}
      {isUploadModalOpen && (
        <ModalComponent
          modalContent={
            <UploadFileForm
              clientOptions={[pdfData.basicInfo.client]}
              wellOptions={[pdfData.basicInfo.well]}
              type="tally"
              fetchDataWells={() => {}}
              initialValues={{
                client: pdfData.basicInfo.client,
                well: pdfData.basicInfo.well,
                customName: pdfData.basicInfo.customName,
                tallyId: savedTallyId,
              }}
              lockClientWell
              onUploadFile={(values: DocumentProps) => handleUploadTally(values)}
              onCancel={() => setIsUploadModalOpen(false)}
            />
          }
        />
      )}
    </>
  );
};
